import { useQuery, useIsMutating } from "@tanstack/react-query";
import { listTables } from "@/api/tables";
import useAppStore from "@/store/useAppStore";
import { useEffect } from "react";

/**
 * Phase G (Δ-11) — 10s polling with mutation-aware pause.
 *
 * Locked decisions:
 *  - Interval = `REACT_APP_POLL_INTERVAL_MS` (default 10_000ms). Δ-11 #1.
 *  - `refetchOnWindowFocus: true` — re-sync on tab focus. Δ-11 #2 (the
 *    pause-on-hidden side is handled by `useTimerTick` for the tick;
 *    TanStack Query already naturally pauses focus refetch when hidden).
 *  - `staleTime: 0` — mount triggers an immediate fresh fetch. Δ-11 #5.
 *  - Polling pauses while ANY mutation is pending (`useIsMutating` > 0).
 *    Returning `false` from `refetchInterval` halts the interval; it
 *    resumes the moment all mutations settle. Δ-11 #6, U-03/U-G-06.
 *
 * See CONSTRAINTS C-18.
 */
const POLL_INTERVAL_MS = Number(
  process.env.REACT_APP_POLL_INTERVAL_MS || 10000
);

export default function useTables() {
  const setTables = useAppStore((s) => s.setTables);
  const isMutating = useIsMutating();

  const query = useQuery({
    queryKey: ["tables"],
    queryFn: listTables,
    staleTime: 0,
    refetchInterval: () => (isMutating > 0 ? false : POLL_INTERVAL_MS),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: isMutating > 0 ? false : true,
  });

  useEffect(() => {
    if (query.data) {
      setTables(query.data);
    }
  }, [query.data, setTables]);

  return query;
}
